import { useCallback, useEffect, useState } from 'react';
import { reportsApi, type SafetyDashboardRow } from '../../services/reportsApi';
import {
  findSafetyDashboardWorkflowRowForReview,
  hasWorkflowContextLookupKeys,
  RISK_PATTERN_WORKFLOW_CONTEXT_FETCH_ERROR_COPY,
  RISK_PATTERN_WORKFLOW_CONTEXT_LIMITED_MODE_COPY,
  resolveRiskPatternWorkflowReviewContext,
  type RiskPatternWorkflowReviewContext,
} from '../../domain/incidents/riskPatternWorkflowReviewContext';

export function useRiskPatternWorkflowReviewContext(args: {
  workflowId: string | null;
  incidentId?: string | null;
  caseId?: string | null;
  deviceId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  riskType?: string | null;
  orgId: string;
  siteId: string;
  reportFrom?: string | null;
  reportTo?: string | null;
  reviewScope?: string | null;
}): {
  context: RiskPatternWorkflowReviewContext | null;
  dashboardRow: SafetyDashboardRow | null;
  loading: boolean;
  error: string | null;
  applyCreatedSafetyCase: (safetyCaseId: string) => void;
} {
  const workflowId = String(args.workflowId || '').trim();
  const incidentId = String(args.incidentId || '').trim();
  const caseId = String(args.caseId || '').trim();
  const hasScopeLookupKeys = Boolean(
    String(args.deviceId || '').trim()
    || String(args.zoneId || '').trim()
    || String(args.cameraId || '').trim()
    || String(args.riskType || '').trim()
  );

  const [context, setContext] = useState<RiskPatternWorkflowReviewContext | null>(null);
  const [dashboardRow, setDashboardRow] = useState<SafetyDashboardRow | null>(null);
  // Do not block Pattern first paint on the dashboard workflow-context fetch.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!args.siteId) {
      setContext(null);
      setDashboardRow(null);
      setLoading(false);
      setError(null);
      return;
    }

    const lookupKeys = hasWorkflowContextLookupKeys({ workflowId, incidentId, caseId });
    if (!lookupKeys && !hasScopeLookupKeys) {
      setContext(null);
      setDashboardRow(null);
      setLoading(false);
      setError(RISK_PATTERN_WORKFLOW_CONTEXT_LIMITED_MODE_COPY);
      return;
    }

    let cancelled = false;
    setError(null);

    // Do not narrow workflow row lookup by the operator's selected review window.
    // A 24h queue filter can drop the linked Safety Case row entirely and hide
    // the after-signed-scan timeline selector on State C review pages.
    reportsApi.getSafetyDashboard({
      org_id: args.orgId || undefined,
      site_id: args.siteId,
      limit: 100,
    }).then((response) => {
      if (cancelled) return;
      const row = findSafetyDashboardWorkflowRowForReview(response.workflows, {
        workflowId,
        incidentId,
        caseId,
        deviceId: args.deviceId,
        zoneId: args.zoneId,
        cameraId: args.cameraId,
        riskType: args.riskType,
      });
      if (!row) {
        setContext(null);
        setDashboardRow(null);
        setError(RISK_PATTERN_WORKFLOW_CONTEXT_LIMITED_MODE_COPY);
        return;
      }
      const resolved = resolveRiskPatternWorkflowReviewContext(row, { reviewScope: args.reviewScope });
      if (!resolved) {
        setContext(null);
        setDashboardRow(null);
        setError(RISK_PATTERN_WORKFLOW_CONTEXT_LIMITED_MODE_COPY);
        return;
      }
      setDashboardRow(row);
      setContext(resolved);
      setError(null);
    }).catch(() => {
      if (cancelled) return;
      setContext(null);
      setDashboardRow(null);
      setError(RISK_PATTERN_WORKFLOW_CONTEXT_FETCH_ERROR_COPY);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    args.orgId,
    args.reportFrom,
    args.reportTo,
    args.reviewScope,
    args.siteId,
    args.deviceId,
    args.zoneId,
    args.cameraId,
    hasScopeLookupKeys,
    args.riskType,
    caseId,
    incidentId,
    workflowId,
  ]);

  const applyCreatedSafetyCase = useCallback((safetyCaseId: string) => {
    const createdId = String(safetyCaseId || '').trim();
    if (!createdId) return;
    setDashboardRow((prev) => {
      const next = {
        ...(prev || {}),
        workflow_id: String(prev?.workflow_id || workflowId || '').trim() || undefined,
        finding_id: createdId,
        safety_case_id: String(prev?.safety_case_id || createdId).trim(),
        finding_saved: true,
      } as SafetyDashboardRow;
      const resolved = resolveRiskPatternWorkflowReviewContext(next, { reviewScope: args.reviewScope });
      if (resolved) {
        setContext(resolved);
        setError(null);
      }
      return next;
    });
  }, [args.reviewScope, workflowId]);

  return { context, dashboardRow, loading, error, applyCreatedSafetyCase };
}
